import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../../../utils/siteSettings';

const HERO_HOME_DEFAULT = 'https://public.readdy.ai/ai/img_res/4ae32e530200a2b07707ecbdb71a9d81.jpg';

const Hero = () => {
  const { t } = useTranslation();
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setOffsetY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-black">
      <div
        className="absolute inset-0 w-full h-full"
        style={{ transform: `translateY(${offsetY * 0.3}px)` }}
      >
        <img
          src={getImageUrl('hero-home', HERO_HOME_DEFAULT)}
          alt="Hero background"
          width={1280}
          height={720}
          className="w-full h-full object-cover object-center scale-110"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40"></div>
      </div>

      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-brand-red/20 rounded-full blur-3xl animate-pulse pointer-events-none"></div>
      <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-brand-red/10 rounded-full blur-2xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }}></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-32">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8">
            <span className="w-2 h-2 bg-brand-red rounded-full animate-pulse"></span>
            <span className="text-white/80 text-sm font-medium tracking-wider uppercase">{t('hero.badge')}</span>
          </div>

          <h1 className="text-6xl md:text-7xl lg:text-8xl font-black text-white leading-none mb-6 tracking-tight">
            {t('hero.title1')}
            <span className="block text-brand-red mt-1">{t('hero.title2')}</span>
            <span className="block text-white/60 text-4xl md:text-5xl font-light mt-2">{t('hero.title3')}</span>
          </h1>

          <p className="text-lg text-white/70 mb-10 max-w-xl leading-relaxed">
            {t('hero.desc')}
          </p>

          <div className="flex flex-wrap gap-4 items-center">
            <Link to="/contact">
              <button className="group px-8 py-4 bg-brand-red hover:bg-brand-red-dark text-white font-bold rounded-lg text-base transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer flex items-center gap-3">
                {t('hero.cta1')}
                <span className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform">
                  <i className="ri-arrow-right-line rtl:rotate-180 text-sm"></i>
                </span>
              </button>
            </Link>
            <Link to="/services">
              <button className="px-8 py-4 border border-white/30 text-white hover:border-white hover:bg-white/10 backdrop-blur-sm font-semibold rounded-lg text-base transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2">
                <i className="ri-play-circle-line text-xl"></i>
                {t('hero.cta2')}
              </button>
            </Link>
          </div>

          <div className="flex flex-wrap gap-8 mt-14 pt-10 border-t border-white/10">
            {[
              { value: '500+', labelKey: 'hero.statClients' },
              { value: '1000+', labelKey: 'hero.statProjects' },
              { value: '50+', labelKey: 'hero.statCountries' },
              { value: '5+', labelKey: 'hero.statYears' },
            ].map((s) => (
              <div key={s.labelKey}>
                <div className="text-3xl font-black text-white">{s.value}</div>
                <div className="text-white/50 text-sm mt-0.5">{t(s.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
    </section>
  );
};

export default Hero;
